import {
  MatterPartyInsuranceSection,
  MatterPartyVehiclesSection,
} from "@/components/features/matter/information/MatterInformationDefendantSection";
import MatterLostWages from "@/components/features/matter/lost-wages/MatterLostWages";
import {
  ActionConfirmationModal,
  Button,
  Card,
  CardContent,
  ServerErrorFallbackCard,
  Text,
} from "@/components/ui";
import { contactRoleSelectPrompt } from "@/components/ui/FormFields/AddContactFormField/AddContactFormField";
import { MatterContactConnectionRichCard } from "@/components/ui/MatterContactConnectionRichCard/MatterContactConnectionRichCard";
import { useUser } from "@/components/UserProvider";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import { useMatterInsuranceVehiclePermissions } from "@/hooks/useMatterInsuranceVehiclePermissions";
import {
  useCreateConnection,
  useDeleteConnection,
} from "@/lib/data/react-query/connections/connection";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { Matter, MatterContactConnection } from "@/lib/types";
import {
  CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS,
  CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS,
} from "@/lib/utils/contact-role-select-card-class";
import { getRemoveMatterContactConnectionModalCopy } from "@/lib/utils/matterContactConnectionHelpers";
import { createMatterPartyConnectionInput } from "@/lib/utils/connections/matter-party-connections";
import type { MatterContactHeroTone } from "@/lib/utils/matters/matter-contact-connection-hero-tone";
import {
  getlabel,
  getMatterContactConnections,
} from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { ErrorBoundary } from "@sentry/react";
import { startCase } from "lodash";
import { PlusCircle } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import toast from "react-hot-toast";
import { MatterInformationIntakeSection } from "./MatterInformationIntakeSection";
import { MatterInformationSurveySection } from "./MatterInformationSurveySection";
import { MatterInjuries } from "./MatterInjuries";

interface MatterInformationClientSectionProps {
  matter: Matter;
  searchLostWageId?: string;
  searchSurveyId?: string;
}

function matterConnectionContactTypeBadges(
  stub: MatterContactConnection["contact"],
): string[] {
  const contactType = startCase(stub.type ?? "");
  return contactType ? [contactType] : [];
}

function MatterContactHeroSlot({
  label,
  connection,
  matter,
  onAdd,
  onEdit,
  onDelete,
  canEdit,
  canAdd,
  tone,
  extraHighlightedBadges,
  showIndividualStatusBadges,
  emptyReadonlyMessage,
  footer,
}: {
  label: string;
  connection?: MatterContactConnection;
  matter: Matter;
  onAdd: () => void;
  onEdit: () => void;
  onDelete?: (id: string, connectionRoleLabel: string) => void;
  canEdit: boolean;
  canAdd: boolean;
  tone: MatterContactHeroTone;
  extraHighlightedBadges?: string[];
  showIndividualStatusBadges?: boolean;
  emptyReadonlyMessage?: string;
  footer?: ReactNode;
}) {
  const roleTestIdSegment = label.toLowerCase().replace(/\s+/g, "-");

  if (connection) {
    return (
      <>
        <MatterContactConnectionRichCard
          contact={connection.contact}
          matterContactConnection={connection}
          matterId={matter.id}
          communicationActions="call-text"
          tone={tone}
          badges={matterConnectionContactTypeBadges(connection.contact)}
          highlightedBadges={extraHighlightedBadges}
          maxVisibleBadges={6}
          showIndividualStatusBadges={showIndividualStatusBadges}
          showEditButton
          testId={`matter-${roleTestIdSegment}-contact-slot`}
          editButtonTestId={`edit-${roleTestIdSegment}-contact-button`}
          onEdit={canEdit ? onEdit : undefined}
          onDelete={
            onDelete
              ? (id: string) => {
                  onDelete(id, label);
                }
              : undefined
          }
          className="w-full min-w-0 border-0 p-0"
        />
        {footer}
      </>
    );
  }

  if (emptyReadonlyMessage) {
    return (
      <div className={CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS}>
        <div className={CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS}>
          <Text
            variant="subHeading"
            color="muted"
            className="pointer-events-none"
          >
            {emptyReadonlyMessage}
          </Text>
        </div>
      </div>
    );
  }

  if (!canAdd) {
    return null;
  }

  return (
    <>
      <div className={CONTACT_ROLE_SELECT_EMPTY_SHELL_CLASS}>
        <button
          type="button"
          className={CONTACT_ROLE_SELECT_EMPTY_BUTTON_CLASS}
          onClick={onAdd}
          aria-label={contactRoleSelectPrompt(label)}
        />
        <div className={CONTACT_ROLE_SELECT_EMPTY_BODY_CLASS}>
          <div className={CONTACT_ROLE_SELECT_EMPTY_CONTENT_CLASS}>
            <span className="bg-muted text-muted-foreground flex h-7 w-7 items-center justify-center rounded-full">
              <PlusCircle className="h-3.5 w-3.5 shrink-0" />
            </span>
            <Text
              variant="body"
              color="foreground"
              className="line-clamp-1 leading-tight"
            >
              {contactRoleSelectPrompt(label)}
            </Text>
          </div>
        </div>
      </div>
      {footer}
    </>
  );
}

const MatterInformationClientSectionDangerous = ({
  matter,
  searchLostWageId,
  searchSurveyId,
}: MatterInformationClientSectionProps) => {
  const { openMatterContactConnectionModal } = useGlobalModal();
  const contactConnections = getMatterContactConnections(matter);
  const clientConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Client",
  );
  const primaryContactConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Primary Contact",
  );
  const spouseConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Spouse",
  );
  const emergencyContactConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Emergency Contact",
  );
  const { hasActionPermission } = useUser();
  const hasUpdateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasCreateMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasDeleteMatterContactConnectionPermission = hasActionPermission(
    ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const hasIntakeTabAccess = Boolean(true);
  const {
    hasCreateInsurancePermission,
    hasViewInsurancePermission,
    hasUpdateInsurancePermission,
    hasCreateVehiclePermission,
    hasViewVehiclePermission,
    hasUpdateVehiclePermission,
  } = useMatterInsuranceVehiclePermissions();
  const createMatterContactConnection = useCreateConnection();
  const deleteMatterContactConnection = useDeleteConnection();

  const [deleteMatterContactConfirmation, setDeleteMatterContactConfirmation] =
    useState<{
      id: string;
      label: string;
    } | null>(null);

  const requestDeleteMatterContactConnection = useCallback(
    (id: string, label: string) => {
      setDeleteMatterContactConfirmation({
        id,
        label,
      });
    },
    [],
  );

  const confirmDeleteMatterContactConnection = useCallback(async () => {
    const pending = deleteMatterContactConfirmation;
    if (!pending) {
      return;
    }
    try {
      await deleteMatterContactConnection.mutateAsync({
        connectionId: pending.id,
        matterId: matter.id,
      });
    } finally {
      setDeleteMatterContactConfirmation(null);
    }
  }, [
    deleteMatterContactConfirmation,
    deleteMatterContactConnection,
    matter.id,
  ]);

  const onDeleteMatterContact = hasDeleteMatterContactConnectionPermission
    ? requestDeleteMatterContactConnection
    : undefined;
  const deleteMatterContactModalCopy = deleteMatterContactConfirmation
    ? getRemoveMatterContactConnectionModalCopy(
        deleteMatterContactConfirmation.label,
      )
    : null;
  const [isCopyingSpouse, setIsCopyingSpouse] = useState(false);
  const showSpouseSection = matter.clientMaritalStatus === "Married";

  const canOpenClientSlideover = clientConnection
    ? hasUpdateMatterContactConnectionPermission
    : hasCreateMatterContactConnectionPermission;

  const handleUseSpouseAsEmergencyContact = async () => {
    if (
      !spouseConnection ||
      emergencyContactConnection ||
      !hasCreateMatterContactConnectionPermission
    ) {
      return;
    }

    setIsCopyingSpouse(true);
    try {
      await createMatterContactConnection.mutateAsync({
        ...createMatterPartyConnectionInput({
          contactId: spouseConnection.contactId,
          matterId: matter.id,
          role: "Emergency Contact",
        }),
        matterId: matter.id,
      });
    } catch {
      toast.error("Unable to copy spouse to emergency contact");
    } finally {
      setIsCopyingSpouse(false);
    }
  };

  const parentConnection = contactConnections.find(
    (connection) => getlabel(connection) === "Parent",
  );
  const clientContactId = clientConnection?.contactId.trim() ?? "";
  const isClientContactConnection = (connection?: MatterContactConnection) =>
    clientContactId !== "" && connection?.contactId.trim() === clientContactId;

  const handleAddParentConnection = () => {
    openMatterContactConnectionModal({
      matterId: matter.id,
      labelOptions: ["Parent"],
      disableTime: true,
      selectContactOnly: true,
      createPresentation: "modal",
      title: "Add parent information",
    });
  };

  const handleEditParentConnection = (connection: MatterContactConnection) => {
    openMatterContactConnectionModal({
      matterId: matter.id,
      matterContactConnection: connection,
      matterContactConnectionContactId: connection.contactId,
      labelOptions: ["Parent"],
      disableTime: true,
      editing: true,
      editPresentation: "modal",
      title: "Edit parent information",
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <Text variant="title">Client</Text>
      </div>

      <Card className="overflow-hidden rounded-[24px] border shadow-sm">
        <CardContent className="p-0">
          <div className="grid gap-0 xl:grid-cols-[380px_minmax(0,1fr)]">
            <div className="border-border/60 bg-card border-r p-6">
              <MatterContactHeroSlot
                label="Client"
                connection={clientConnection}
                matter={matter}
                tone="green"
                extraHighlightedBadges={
                  matter.clientNoSocialSecurity ? ["No Client SSN"] : undefined
                }
                showIndividualStatusBadges={isClientContactConnection(
                  clientConnection,
                )}
                emptyReadonlyMessage={
                  !clientConnection && !canOpenClientSlideover
                    ? "No client added yet."
                    : undefined
                }
                onAdd={() => {
                  openMatterContactConnectionModal({
                    matterId: matter.id,
                    matterContactConnectionContactId: "",
                    matterContactConnection: undefined,
                    disableDelete: true,
                    labelOptions: ["Client"],
                    disableTime: true,
                    title: "Add client information",
                    autoOpenContactSelection: true,
                    createPresentation: "modal",
                  });
                }}
                onEdit={() => {
                  if (!clientConnection) {
                    return;
                  }
                  openMatterContactConnectionModal({
                    matterId: matter.id,
                    matterContactConnectionContactId:
                      clientConnection.contactId,
                    matterContactConnection: clientConnection,
                    disableDelete: true,
                    labelOptions: ["Client"],
                    disableTime: true,
                    editing: true,
                    editPresentation: "modal",
                    title: "Edit client information",
                  });
                }}
                canEdit={hasUpdateMatterContactConnectionPermission}
                canAdd={hasCreateMatterContactConnectionPermission}
              />

              <div className="mt-6 space-y-3">
                <Text variant="caption" color="primary" className="font-medium">
                  Related contacts
                </Text>
                <MatterContactHeroSlot
                  label="Parent"
                  connection={parentConnection}
                  matter={matter}
                  tone="gray"
                  onDelete={onDeleteMatterContact}
                  showIndividualStatusBadges={isClientContactConnection(
                    parentConnection,
                  )}
                  onAdd={handleAddParentConnection}
                  onEdit={() => {
                    if (parentConnection) {
                      handleEditParentConnection(parentConnection);
                    }
                  }}
                  canEdit={hasUpdateMatterContactConnectionPermission}
                  canAdd
                />

                <MatterContactHeroSlot
                  label="Primary contact"
                  connection={primaryContactConnection}
                  matter={matter}
                  tone="gray"
                  onDelete={onDeleteMatterContact}
                  showIndividualStatusBadges={isClientContactConnection(
                    primaryContactConnection,
                  )}
                  onAdd={() => {
                    openMatterContactConnectionModal({
                      matterId: matter.id,
                      labelOptions: ["Primary Contact"],
                      disableTime: true,
                      selectContactOnly: true,
                      createPresentation: "modal",
                      title: "Add primary contact",
                    });
                  }}
                  onEdit={() => {
                    if (!primaryContactConnection) {
                      return;
                    }

                    openMatterContactConnectionModal({
                      matterContactConnectionContactId:
                        primaryContactConnection.contactId,
                      matterContactConnection: primaryContactConnection,
                      matterId: matter.id,
                      labelOptions: ["Primary Contact"],
                      disableTime: true,
                      editing: true,
                      editPresentation: "modal",
                      title: "Edit primary contact information",
                    });
                  }}
                  canEdit={hasUpdateMatterContactConnectionPermission}
                  canAdd
                />

                {showSpouseSection && (
                  <MatterContactHeroSlot
                    label="Spouse"
                    connection={spouseConnection}
                    matter={matter}
                    tone="gray"
                    onDelete={onDeleteMatterContact}
                    showIndividualStatusBadges={isClientContactConnection(
                      spouseConnection,
                    )}
                    onAdd={() => {
                      openMatterContactConnectionModal({
                        matterId: matter.id,
                        labelOptions: ["Spouse"],
                        disableTime: true,
                        selectContactOnly: true,
                        createPresentation: "modal",
                        title: "Add spouse information",
                      });
                    }}
                    onEdit={() => {
                      if (!spouseConnection) {
                        return;
                      }

                      openMatterContactConnectionModal({
                        matterContactConnectionContactId:
                          spouseConnection.contactId,
                        matterContactConnection: spouseConnection,
                        matterId: matter.id,
                        labelOptions: ["Spouse"],
                        disableTime: true,
                        editing: true,
                        editPresentation: "modal",
                        title: "Edit spouse information",
                      });
                    }}
                    canEdit={hasUpdateMatterContactConnectionPermission}
                    canAdd
                  />
                )}

                <MatterContactHeroSlot
                  label="Emergency contact"
                  connection={emergencyContactConnection}
                  matter={matter}
                  tone="gray"
                  onDelete={onDeleteMatterContact}
                  showIndividualStatusBadges={isClientContactConnection(
                    emergencyContactConnection,
                  )}
                  onAdd={() => {
                    openMatterContactConnectionModal({
                      matterId: matter.id,
                      labelOptions: ["Emergency Contact"],
                      disableTime: true,
                      selectContactOnly: true,
                      createPresentation: "modal",
                      title: "Add emergency contact",
                    });
                  }}
                  onEdit={() => {
                    if (!emergencyContactConnection) {
                      return;
                    }

                    openMatterContactConnectionModal({
                      matterContactConnectionContactId:
                        emergencyContactConnection.contactId,
                      matterContactConnection: emergencyContactConnection,
                      matterId: matter.id,
                      labelOptions: ["Emergency Contact"],
                      disableTime: true,
                      editing: true,
                      editPresentation: "modal",
                      title: "Edit emergency contact information",
                    });
                  }}
                  canEdit={hasUpdateMatterContactConnectionPermission}
                  canAdd
                  footer={
                    !emergencyContactConnection &&
                    showSpouseSection &&
                    spouseConnection &&
                    hasCreateMatterContactConnectionPermission ? (
                      <Button
                        variant="ghost"
                        className="px-0"
                        size="sm"
                        onClick={() => {
                          void handleUseSpouseAsEmergencyContact();
                        }}
                        disabled={
                          createMatterContactConnection.isPending ||
                          isCopyingSpouse
                        }
                      >
                        Use spouse as emergency contact
                      </Button>
                    ) : undefined
                  }
                />
              </div>
            </div>

            <div className="bg-card p-6 lg:p-8">
              <div className="space-y-7">
                {(!matter.incidentType ||
                  matter.incidentType.lostWagesModuleAccess) && (
                  <MatterLostWages
                    matter={matter}
                    showHighlights={false}
                    showTitle={true}
                    titleText="Employment history"
                    searchLostWageId={searchLostWageId}
                  />
                )}

                {clientConnection && (
                  <MatterPartyInsuranceSection
                    matter={matter}
                    partyConnection={clientConnection}
                    partyRole="Client"
                    canViewInsurance={hasViewInsurancePermission}
                    canCreateInsurance={hasCreateInsurancePermission}
                    canEditInsurance={hasUpdateInsurancePermission}
                    includeUnassigned
                  />
                )}

                {clientConnection && (
                  <MatterPartyVehiclesSection
                    matter={matter}
                    partyConnection={clientConnection}
                    partyRole="Client"
                    canViewVehicles={hasViewVehiclePermission}
                    canCreateVehicle={hasCreateVehiclePermission}
                    canEditVehicle={hasUpdateVehiclePermission}
                    includeUnassigned
                  />
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {(!matter.incidentType || matter.incidentType.injuriesModuleAccess) && (
        <Card className="border-border h-min rounded-[24px] border shadow-none">
          <CardContent className="bg-card p-4 sm:p-5">
            <MatterInjuries matter={matter} noBorder />
          </CardContent>
        </Card>
      )}
      {(!matter.incidentType ||
        matter.incidentType.intakeResponsesModuleAccess) &&
        hasIntakeTabAccess && (
          <div>
            <MatterInformationIntakeSection matter={matter} />
          </div>
        )}

      <MatterInformationSurveySection
        matter={matter}
        requestedSurveyId={searchSurveyId}
      />

      <ActionConfirmationModal
        open={deleteMatterContactConfirmation != null}
        setOpen={(open) => {
          if (!open && !deleteMatterContactConnection.isPending) {
            setDeleteMatterContactConfirmation(null);
          }
        }}
        title={deleteMatterContactModalCopy?.title}
        description={deleteMatterContactModalCopy?.description}
        buttonText={deleteMatterContactModalCopy?.buttonText}
        isActionPending={deleteMatterContactConnection.isPending}
        afterActionFn={() => {
          void confirmDeleteMatterContactConnection();
        }}
      />
    </div>
  );
};

export function MatterInformationClientSection(
  props: MatterInformationClientSectionProps,
) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterInformationClientSectionDangerous {...props} />
    </ErrorBoundary>
  );
}
